import { useCallback, useEffect, useState } from 'react';
import PropTypes, { number, string } from 'prop-types';

import { Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { TextField } from 'mui-rff';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

import MultiSelect, {
  REASON_CLEAR,
  REASON_REMOVE_OPTION,
  REASON_SELECT_OPTION,
} from '@components/MultiSelect';

export const ACTIONS = {
  ADD: 'ADD',
  EDIT: 'EDIT',
};

export default function PropertiesForm({
  action,
  currencies,
  formId,
  initialValues,
  gateways,
  merchants,
  methods,
  properties,
  handleOnGatewayChange = () => {},
  handleOnFormSubmit = () => {},
  validateForm = () => {},
  ...rest
}) {
  const [selectedProperties, setSelectedProperties] = useState([]);

  const handleOnPropertiesChange = useCallback(
    (values, reason, formValues, setValue, concat) => {
      const PROPERTY_VALUES_KEY = 'propertyValues';
      if (reason === REASON_REMOVE_OPTION) {
        const selectedIds = values.map((v) => v.id);
        const filtered = formValues.propertyValues.filter((prop) =>
          selectedIds.includes(prop.id),
        );

        setValue(PROPERTY_VALUES_KEY, filtered || []);
      } else if (reason === REASON_SELECT_OPTION) {
        const addedIds = formValues.propertyValues
          ? formValues.propertyValues.map((value) => value.id)
          : [];
        const newlySelectedProps = values.filter(
          (value) => !addedIds.includes(value.id),
        );
        const newPropsValues = newlySelectedProps.map((prop) => ({
          ...prop,
          value: '',
        }));

        concat(PROPERTY_VALUES_KEY, newPropsValues);
      } else if (reason === REASON_CLEAR) {
        setValue(PROPERTY_VALUES_KEY, []);
      }
      setSelectedProperties(values);
    },
    [],
  );

  useEffect(() => {
    // sets selected props in multiselect
    setSelectedProperties(initialValues.properties);
  }, [initialValues]);

  return (
    <Form
      onSubmit={handleOnFormSubmit}
      validate={validateForm}
      validateOnBlur
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setValue: ([field, value], state, { changeValue }) => {
          changeValue(state, field, () => value);
        },
      }}
      render={({
        values: formValues,
        form: {
          mutators: { concat, remove, setValue },
        },
        handleSubmit,
      }) => (
        <form
          id={formId}
          onSubmit={handleSubmit}
          noValidate>
          <Grid
            id="propertiesFormWrapper"
            container
            spacing={2}>
            <Grid
              item
              id="merchantWrapper"
              xs={12}>
              <MultiSelect
                id="merchantSelect"
                name="merchants"
                label="Select platform"
                multiple={false}
                readOnly
                required
                value={merchants[0]}
                items={merchants}
                isOptionEqualToValue={(option, value) =>
                  option.platformId === value.platformId
                }
                optionDisplayKey="platformName"
              />
            </Grid>
            <Grid
              item
              id="currencyWrapper"
              xs={12}>
              <MultiSelect
                id="currencySelect"
                name="currencies"
                label="Select currency"
                multiple={false}
                readOnly
                required
                value={currencies[0]}
                items={currencies}
                getOptionLabel={(option) => option}
              />
            </Grid>
            <Grid
              item
              id="methodsWrapper"
              xs={12}>
              <MultiSelect
                id="methodSelect"
                name="methods"
                required
                multiple={action === ACTIONS.ADD}
                readOnly={action === ACTIONS.EDIT}
                items={methods}
                value={action === ACTIONS.EDIT ? initialValues.methods : null}
                isOptionEqualToValue={(option, value) =>
                  option.methodId === value.methodId
                }
                label="Select methods"
                optionDisplayKey="methodCode"
                customOptionLabel={(option) =>
                  `${option.methodName} (${option.methodCode})`
                }
                customFilterStringify={(option) =>
                  `${option.methodName} (${option.methodCode})`
                }
              />
            </Grid>
            <Grid
              item
              id="gatewayWrapper"
              xs={12}>
              <MultiSelect
                id="gatewaySelect"
                name="gateway"
                required
                disableSelectAll
                multiple={false}
                readOnly={false}
                items={gateways}
                value={action === ACTIONS.EDIT ? initialValues.gateways : null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                label="Select gateway"
                onChange={handleOnGatewayChange}
              />
            </Grid>
            {(formValues.gateway || action === ACTIONS.EDIT) && (
              <Grid
                container
                item
                spacing={2}>
                <Grid
                  item
                  id="propertiesSelectWrapper"
                  xs={12}>
                  <MultiSelect
                    id="propertiesSelect"
                    name="properties"
                    disableSelectAll
                    required
                    items={properties}
                    value={selectedProperties}
                    onChange={(values, reason) =>
                      handleOnPropertiesChange(
                        values,
                        reason,
                        formValues,
                        setValue,
                        concat,
                      )
                    }
                    label={
                      selectedProperties && selectedProperties.length > 0
                        ? 'Select properties and fill-out values'
                        : 'Select properties'
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    optionDisplayKey="key"
                    customOptionLabel={(option) =>
                      `${option.description} (${option.key})`
                    }
                  />
                </Grid>
              </Grid>
            )}
            {formValues.properties && (
              <Grid
                id="propertyValuesWrapper"
                item
                xs={12}>
                <FieldArray name="propertyValues">
                  {({ fields, fields: { value: fieldValue } }) =>
                    fields.map((name, index) => (
                      <Grid
                        container
                        alignItems="center"
                        spacing={1}
                        style={{ marginBottom: '16px' }}
                        key={name}>
                        <Grid
                          item
                          xs={8}>
                          <TextField
                            required
                            name={`${name}.value`}
                            label={`${fieldValue[index]?.description} (${fieldValue[index]?.key})`}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}>
                          <Button
                            size="large"
                            onClick={() => {
                              fields.remove(index);
                              remove('properties', index);
                              setSelectedProperties((props) =>
                                props.filter(
                                  (prop, propIndex) => propIndex !== index,
                                ),
                              );
                            }}>
                            ❌
                          </Button>
                        </Grid>
                      </Grid>
                    ))
                  }
                </FieldArray>
              </Grid>
            )}
          </Grid>
        </form>
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
}

PropertiesForm.propTypes = {
  /**
   * Form ID.
   */
  formId: PropTypes.string,

  /**
   * Form ID.
   */
  initialValues: PropTypes.shape(),

  /**
   * List of methods to select from.
   */
  methods: PropTypes.arrayOf(
    PropTypes.shape({
      methodId: number,
      methodCode: string,
      methodName: string,
    }),
  ),
  /**
   * List of merchants to select from.
   */
  merchants: PropTypes.arrayOf(
    PropTypes.shape({
      merchantId: number,
      platformId: number,
      platformCode: string,
      platformName: string,
    }),
  ),
  /**
   * List of currencies to select from.
   */
  currencies: PropTypes.arrayOf(PropTypes.string),
  /**
   * List of gateways to select from.
   */
  gateways: PropTypes.arrayOf(
    PropTypes.shape({
      id: number,
      code: string,
      name: string,
    }),
  ),
  /**
   * List of gateway properties to select from.
   */
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      id: number,
      key: string,
      description: string,
    }),
  ),
  /**
   * Callback for when selected gateway changes.
   * Used on edit mode.
   */
  handleOnGatewayChange: PropTypes.func,
  /**
   * Callback when form is submitted.
   */
  handleOnFormSubmit: PropTypes.func,
  /**
   * Custom form validation.
   */
  validateForm: PropTypes.func,
};

PropertiesForm.defaultProps = {
  formId: 'propertiesForm',
  initialValues: {},
  methods: [],
  gateways: [],
  properties: [],
  merchants: [],
  currencies: [],
  handleOnGatewayChange: () => {},
  handleOnFormSubmit: () => {},
  validateForm: () => {},
};
